/**
 * CustomBlockTable — renders a RenderBlockTable (a custom "scan sheet" table)
 * as a Bootstrap grid of cards. Each card is a row of cells; each cell stacks
 * typed blocks (text line, barcode, qty badge, spacer) with per-block styling.
 *
 * Same descriptor is consumed by the PDF (drawBlockTable in pdf.ts), so the
 * screen, print, and PDF outputs stay consistent.
 */

import React, { useEffect, useRef } from 'react';
import { CellStyle } from '../../types';
import { RenderBlockTable, ResolvedBlock, ResolvedCard } from '../../utils/renderModel';
import { trackOutputTableViewed } from '../../utils/gtmTracking';

interface Props {
  table: RenderBlockTable;
}

function styleToCss(style?: CellStyle): React.CSSProperties {
  if (!style) return {};
  return {
    fontSize: style.fontSizePx ? `${style.fontSizePx}px` : undefined,
    fontWeight: style.fontWeight,
    color: style.color,
    background: style.background,
    textAlign: style.align,
  };
}

const Block: React.FC<{ block: ResolvedBlock }> = ({ block }) => {
  // A wrapper carries the block's alignment so inline content (qty badge,
  // barcode image) honors center/right too — not just text lines.
  const align = block.style?.align ?? 'left';
  switch (block.type) {
    case 'textLine':
      return <div style={styleToCss(block.style)}>{block.text}</div>;
    case 'qtyBadge':
      return (
        <div style={{ textAlign: align }}>
          <span
            className="custom-table-qty-badge"
            style={{
              display: 'inline-block',
              padding: '2px 10px',
              borderRadius: '999px',
              fontWeight: block.style?.fontWeight || 'bold',
              background: block.style?.background || '#3a4b5c',
              color: block.style?.color || '#fff',
              fontSize: block.style?.fontSizePx ? `${block.style.fontSizePx}px` : undefined,
            }}
          >
            {block.text}
          </span>
        </div>
      );
    case 'barcode':
      return block.barcode ? (
        <div style={{ textAlign: align }}>
          <img
            src={block.barcode.src}
            alt={block.barcode.alt}
            style={{ height: block.barcode.heightPx ? `${block.barcode.heightPx + 16}px` : '56px', width: 'auto', maxWidth: '100%' }}
          />
        </div>
      ) : null;
    case 'spacer':
    default:
      return <div style={{ height: block.style?.fontSizePx ? `${block.style.fontSizePx}px` : '8px' }} />;
  }
};

const Card: React.FC<{ card: ResolvedCard; widthClass: string }> = ({ card, widthClass }) => (
  <div className={widthClass}>
    <div className="custom-table-card border rounded-3 p-2 mb-3 h-100" style={{ background: card.background }}>
      <div className="row g-2">
        {card.cells.map((cell, ci) => (
          <div
            key={ci}
            className={`col-${Math.max(1, Math.min(12, cell.colSpan))}`}
            style={{ background: cell.background }}
          >
            {cell.blocks.map((b, bi) => (
              <Block key={bi} block={b} />
            ))}
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const CustomBlockTable: React.FC<Props> = ({ table }) => {
  // Analytics: report the table once per mount, when at least half of it is on
  // screen. A table with barcode blocks is what marketing calls a scan sheet.
  const hostRef = useRef<HTMLDivElement>(null);
  const reportedRef = useRef(false);
  const hasBarcodes = table.cards.some(c => c.cells.some(cell => cell.blocks.some(b => b.type === 'barcode')));
  const skuCount = table.cards.length;
  useEffect(() => {
    const el = hostRef.current;
    if (!el || reportedRef.current || !table.visibleBrowser) return;
    const report = () => {
      if (reportedRef.current) return;
      reportedRef.current = true;
      trackOutputTableViewed({ tableKey: table.key, title: table.title, placement: table.placement, hasBarcodes, skuCount });
    };
    if (typeof IntersectionObserver === 'undefined') { report(); return; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting)) { report(); io.disconnect(); }
    }, { threshold: 0.5 });
    io.observe(el);
    return () => io.disconnect();
  }, [table.key, table.title, table.placement, table.visibleBrowser, hasBarcodes, skuCount]);

  if (table.cards.length === 0) return null;

  // Browser/print visibility → Bootstrap display utilities.
  let visClass = '';
  if (!table.visibleBrowser && !table.visiblePrint) return null;
  if (table.visibleBrowser && !table.visiblePrint) visClass = 'd-print-none';
  else if (!table.visibleBrowser && table.visiblePrint) visClass = 'd-none d-print-block';

  const unit = Math.max(1, Math.min(12, Math.round(12 / Math.max(1, Math.min(4, table.cardsPerRow)))));
  const widthClass = `col-${unit}`;

  return (
    <div ref={hostRef} className={`custom-block-table mb-4 ${visClass}`}>
      {table.title && <h3 className="materials-section-title">{table.title}</h3>}
      <div className="row g-2">
        {table.cards.map((card, i) => (
          <Card key={i} card={card} widthClass={widthClass} />
        ))}
      </div>
    </div>
  );
};

export default CustomBlockTable;
